import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    fetchStoresForDeliveryAreas,
    fetchAreasForStore,
    updateStoreAreaAssignment,
} from "../../service/api/orders"; // adjust path to wherever api.js lives

function IconSearch(props) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}

function IconCheck(props) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

/**
 * Two-step flow:
 *  1. Pick a store from a grid of every store (visible at once, no
 *     hunting through a cramped scrolling dropdown).
 *  2. See every delivery area, with the ones already assigned to that store
 *     pre-checked and tagged "Currently assigned". Toggle any row, then Save.
 *
 * onSaved(store, areaIds) fires after a successful save, before the modal closes.
 */
export default function AssignAreasToStoreModal({ open, onClose, onSaved }) {
    const [stores, setStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const [storesError, setStoresError] = useState(null);
    const [storeQuery, setStoreQuery] = useState("");
    const [selectedStore, setSelectedStore] = useState(null);
    const [step, setStep] = useState("store"); // "store" | "areas"

    const [areas, setAreas] = useState([]);
    const [assignedIds, setAssignedIds] = useState(new Set());
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [areasLoading, setAreasLoading] = useState(false);
    const [areasError, setAreasError] = useState(null);
    const [areaQuery, setAreaQuery] = useState("");

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Fresh state every time the modal opens.
    useEffect(() => {
        if (!open) return;

        setSelectedStore(null);
        setStep("store");
        setAreas([]);
        setAssignedIds(new Set());
        setSelectedIds(new Set());
        setStoreQuery("");
        setAreaQuery("");
        setSaveError(null);

        let cancelled = false;
        setStoresLoading(true);
        setStoresError(null);

        fetchStoresForDeliveryAreas()
            .then((res) => {
                if (cancelled) return;
                setStores(res.data || []);
            })
            .catch(() => {
                if (!cancelled) setStoresError("Couldn't load stores.");
            })
            .finally(() => {
                if (!cancelled) setStoresLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open]);

    const filteredStores = useMemo(() => {
        const q = storeQuery.trim().toLowerCase();
        const list = q ? stores.filter((s) => s.name.toLowerCase().includes(q)) : stores;

        return [...list].sort((a, b) => {
            const aFirst = selectedStore && a.id === selectedStore.id ? 0 : 1;
            const bFirst = selectedStore && b.id === selectedStore.id ? 0 : 1;
            if (aFirst !== bFirst) return aFirst - bFirst;
            return a.name.localeCompare(b.name);
        });
    }, [stores, storeQuery, selectedStore]);

    const loadAreasForStore = useCallback((store) => {
        setSelectedStore(store);
        setStep("areas");
        setAreaQuery("");
        setAreasLoading(true);
        setAreasError(null);

        fetchAreasForStore(store.id)
            .then((res) => {
                const data = res.data || {};
                const allAreas = data.areas || [];
                const assigned = new Set((data.assigned_area_ids || []).map(String));
                setAreas(allAreas);
                setAssignedIds(assigned);
                setSelectedIds(new Set(assigned));
            })
            .catch(() => {
                setAreasError("Couldn't load areas for this store.");
                setAreas([]);
                setAssignedIds(new Set());
                setSelectedIds(new Set());
            })
            .finally(() => setAreasLoading(false));
    }, []);

    const toggleArea = (id) => {
        const key = String(id);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Assigned areas float to the top so the "what's already on" state is
    // always visible first, regardless of the live selection while toggling.
    const filteredAreas = useMemo(() => {
        const q = areaQuery.trim().toLowerCase();
        const list = q
            ? areas.filter(
                (a) =>
                    a.name.toLowerCase().includes(q) ||
                    (a.description || "").toLowerCase().includes(q)
            )
            : areas;

        return [...list].sort((a, b) => {
            const aFirst = assignedIds.has(String(a.id)) ? 0 : 1;
            const bFirst = assignedIds.has(String(b.id)) ? 0 : 1;
            if (aFirst !== bFirst) return aFirst - bFirst;
            return a.name.localeCompare(b.name);
        });
    }, [areas, areaQuery, assignedIds]);

    const hasChanges = useMemo(() => {
        if (selectedIds.size !== assignedIds.size) return true;
        for (const id of selectedIds) {
            if (!assignedIds.has(id)) return true;
        }
        return false;
    }, [selectedIds, assignedIds]);

    const handleClose = () => {
        if (saving) return;
        onClose && onClose();
    };

    const handleChangeStore = () => {
        // Just switch views — selectedStore stays remembered so the grid can
        // highlight it and pin it to the top instead of forgetting the pick.
        setStep("store");
        setAreaQuery("");
    };

    const handleSave = async () => {
        if (!selectedStore) return;
        setSaving(true);
        setSaveError(null);
        try {
            const areaIds = Array.from(selectedIds).map(Number);
            await updateStoreAreaAssignment({
                id: selectedStore.id,
                delivery_areas: areaIds,
                stores_to_area: false, // id refers to the store, not an area
            });
            onSaved && onSaved(selectedStore, areaIds);
            onClose && onClose();
        } catch (e) {
            setSaveError("Couldn't save — please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="asa-overlay" onMouseDown={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="asa-modal asa-modal--wide">
                <div className="asa-header">
                    <div>
                        <h2>Assign Areas to Store</h2>
                        <p className="asa-subtitle">
                            {selectedStore
                                ? <>Choosing delivery areas for <strong>{selectedStore.name}</strong>.</>
                                : "Pick a store, then choose which delivery areas should serve it."}
                        </p>
                    </div>
                    <button className="asa-close" onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {/* Step 1: store grid — every store visible at once, no dropdown */}
                {step === "store" && (
                    <div className="asa-store-step">
                        <div className="asa-search-wrap asa-store-search">
                            <IconSearch className="asa-search-icon" />
                            <input
                                autoFocus
                                className="asa-search-input"
                                placeholder="Search stores…"
                                value={storeQuery}
                                onChange={(e) => setStoreQuery(e.target.value)}
                            />
                        </div>

                        <div className="asa-store-grid">
                            {storesLoading && <div className="asa-state">Loading stores…</div>}
                            {storesError && <div className="asa-state asa-state-error">{storesError}</div>}
                            {!storesLoading && !storesError && filteredStores.length === 0 && (
                                <div className="asa-state">No stores match "{storeQuery}".</div>
                            )}
                            {!storesLoading &&
                                filteredStores.map((s) => {
                                    const isPicked = selectedStore?.id === s.id;
                                    return (
                                        <button
                                            type="button"
                                            key={s.id}
                                            className={`asa-store-chip${isPicked ? " is-selected" : ""}`}
                                            onClick={() => loadAreasForStore(s)}
                                            title={s.name}
                                        >
                                            <span className="asa-store-chip-name">{s.name}</span>
                                            {isPicked && (
                                                <span className="asa-store-chip-check" aria-hidden="true">
                                                    <IconCheck />
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                )}

                {/* Step 2: areas checklist for the chosen store */}
                {step === "areas" && selectedStore && (
                    <div className="asa-areas-section">
                        <div className="asa-areas-header">
                            <label className="asa-label">
                                Delivery areas for <strong>{selectedStore.name}</strong>
                            </label>
                            <div className="asa-areas-header-right">
                                <span className="asa-count">
                                    {selectedIds.size} selected / {areas.length} total
                                </span>
                                <button type="button" className="asa-change-store" onClick={handleChangeStore}>
                                    Change store
                                </button>
                            </div>
                        </div>

                        <div className="asa-search-wrap asa-areas-search">
                            <IconSearch className="asa-search-icon" />
                            <input
                                className="asa-search-input"
                                placeholder="Filter areas…"
                                value={areaQuery}
                                onChange={(e) => setAreaQuery(e.target.value)}
                            />
                        </div>

                        <div className="asa-areas-list">
                            {areasLoading && <div className="asa-state">Loading areas…</div>}
                            {areasError && <div className="asa-state asa-state-error">{areasError}</div>}
                            {!areasLoading && !areasError && filteredAreas.length === 0 && (
                                <div className="asa-state">No delivery areas match that search.</div>
                            )}
                            {!areasLoading &&
                                filteredAreas.map((area) => {
                                    const key = String(area.id);
                                    const checked = selectedIds.has(key);
                                    const wasAssigned = assignedIds.has(key);
                                    return (
                                        <label
                                            key={area.id}
                                            className={`asa-area-row${checked ? " is-checked" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleArea(area.id)}
                                            />
                                            <span className="asa-area-checkbox" aria-hidden="true">
                                                {checked && <IconCheck />}
                                            </span>
                                            <span className="asa-area-info">
                                                <span className="asa-area-name">{area.name}</span>
                                                {area.description && (
                                                    <span className="asa-area-desc">{area.description}</span>
                                                )}
                                            </span>
                                            {wasAssigned && (
                                                <span className="asa-area-tag">Currently assigned</span>
                                            )}
                                        </label>
                                    );
                                })}
                        </div>
                    </div>
                )}

                {saveError && <div className="asa-save-error">{saveError}</div>}

                <div className="asa-footer">
                    <button className="ana-btn ana-btn-ghost" onClick={handleClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        className="ana-btn ana-btn-primary"
                        onClick={handleSave}
                        disabled={!selectedStore || !hasChanges || saving || areasLoading}
                    >
                        {saving ? "Saving…" : "Save assignment"}
                    </button>
                </div>
            </div>
        </div>
    );
}